import { Alert, Box, Button, Card, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionButton, PermissionGuard } from 'src/routes/components';
import ErrorPage403 from 'src/pages/error/403';
import {
  approveTransfer,
  cancelTransfer,
  getTransfer,
  listTransfers,
  reconcileTransfer,
  rejectTransfer,
} from '../../../services/financeTransferService';
import { MoneyRequest } from '../../../services/moneyRequestApi';
import {
  REQUEST_STATUSES,
  RequestsTable,
  describePayload,
  isUnresolved,
} from '../../money-requests/RequestsTable';
import { useCurrentUserId, useRequestList } from '../../money-requests/useMoneyRequests';

const View = () => {
  const t = useTranslate();
  const notify = useNotify();
  const currentUserId = useCurrentUserId();
  const [status, setStatus] = useState('');
  const { rows, loading, busyId, load, run, setBusyId } = useRequestList(listTransfers, status);
  // Transfers whose approval was sent and whose result is not known yet: Approve stays hidden for them.
  const [attempted, setAttempted] = useState<Set<string>>(new Set());

  const unresolvedHint = t('module.financeTransfers.unresolvedHint', {
    _: "Outcome unknown - do NOT request a replacement. Advance the source wallet's nonce (runbook), then Reconcile.",
  });

  /**
   * Approve = the server signs and broadcasts once. It can take a minute and a half, and Cloudflare cuts
   * a request at 100 s while the server carries on. So after ANY error or timeout the record is read
   * again and its status shown; Approve is never offered again for it.
   */
  const approve = async (r: MoneyRequest) => {
    const amount = `${r.payload.amount} ${r.payload.asset}`;
    const ok = window.confirm(
      t('module.financeTransfers.approveConfirm', {
        _: 'Approve and send %{amount} to %{to}? The server will sign and broadcast it once.',
        amount,
        to: r.payload.toAddress,
      })
    );
    if (!ok) return;
    setBusyId(r.id);
    setAttempted((s) => new Set(s).add(r.id));
    try {
      const res = await approveTransfer(r.id);
      notify(
        `${res.status}${res.txHash ? ` - tx ${res.txHash}` : ''}${res.error ? ` - ${res.error}` : ''}`,
        {
          type:
            res.status === 'EXECUTED' ? 'success' : res.status === 'FAILED' ? 'error' : 'warning',
        }
      );
    } catch (e: any) {
      let current = '';
      try {
        const fresh = await getTransfer(r.id);
        current = ` ${t('module.financeTransfers.currentStatus', { _: 'Current status' })}: ${fresh.status}.`;
        // The server refused (4xx) and nothing was claimed: it is still PENDING, so it may be approved again.
        if (fresh.status === 'PENDING' && e?.status && e.status >= 400 && e.status < 500) {
          setAttempted((s) => {
            const next = new Set(s);
            next.delete(r.id);
            return next;
          });
        }
      } catch {
        current = ` ${t('module.financeTransfers.statusUnreadable', {
          _: 'The status could not be read; reload the list before doing anything else.',
        })}`;
      }
      // SOURCE_WALLET_BUSY / DESTINATION_HAS_UNRESOLVED_TRANSFER messages already carry the runbook advice.
      notify(`${e?.message || 'Error'}${current}`, { type: 'error' });
    } finally {
      setBusyId(null);
      load();
    }
  };

  const reconcile = async (r: MoneyRequest) => {
    setBusyId(r.id);
    try {
      const res = await reconcileTransfer(r.id);
      notify(`${t('module.financeTransfers.reconciled', { _: 'Reconciled' })}: ${res.status}`, {
        type: res.status === 'EXECUTED' ? 'success' : 'info',
      });
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    } finally {
      setBusyId(null);
      load();
    }
  };

  return (
    <PermissionGuard resource="finance-transfer" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs heading={t('resource.financeTransfers', { _: 'Transfer requests' })} />
        <Card sx={{ mt: 3, p: 2 }}>
          <Stack spacing={2}>
            <Alert severity="info" variant="outlined">
              {t('module.financeTransfers.intro', {
                _: 'A transfer is requested by one admin and approved by a different admin. The server signs and broadcasts it once; the signed transaction is never shown.',
              })}
            </Alert>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                select
                size="small"
                label={t('module.moneyRequests.status', { _: 'Status' })}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">{t('module.moneyRequests.all', { _: 'All' })}</MenuItem>
                {REQUEST_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ flex: 1 }} />
              <Button variant="outlined" onClick={load} disabled={loading}>
                {t('module.moneyRequests.reload', { _: 'Reload' })}
              </Button>
            </Stack>
            <RequestsTable
              rows={rows}
              loading={loading}
              currentUserId={currentUserId}
              busyId={busyId}
              attemptedIds={attempted}
              approvePermission={{ resource: 'finance-transfer', action: 'approve' }}
              cancelPermission={{ resource: 'finance-transfer', action: 'request' }}
              unresolvedHint={unresolvedHint}
              summary={(r) => (
                <Typography variant="body2" component="span">
                  {r.payload.amount} {r.payload.asset} {r.payload.chain}
                  <br />
                  {describePayload(r.payload, ['fromAddress', 'toAddress'])}
                </Typography>
              )}
              onApprove={approve}
              onReject={(r, reason) =>
                run(
                  r.id,
                  () => rejectTransfer(r.id, reason),
                  t('module.moneyRequests.rejected', { _: 'Rejected' })
                )
              }
              onCancel={(r) =>
                run(
                  r.id,
                  () => cancelTransfer(r.id),
                  t('module.moneyRequests.cancelled', { _: 'Cancelled' })
                )
              }
              extraActions={(r) =>
                isUnresolved(r) ? (
                  <PermissionButton resource="finance-transfer" action="approve">
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      disabled={busyId === r.id}
                      onClick={() => reconcile(r)}
                    >
                      {t('module.financeTransfers.reconcile', { _: 'Reconcile' })}
                    </Button>
                  </PermissionButton>
                ) : null
              }
            />
          </Stack>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
};

export default View;
